/**
 * Le dock « Demander à Hector » suit Hector dans le canvas de son fil (#1588).
 *
 * Le dock n'offre aucun emplacement au canvas (#860) : un acte, un courrier ou
 * une réécriture lancés depuis lui s'écrivaient sans que l'avocat ne voie rien.
 * La consigne « propose le bouton » du prompt (#861) n'y suffisait pas — le
 * modèle la contournait, et quand il obéissait l'avocat devait cliquer puis
 * reformuler. L'écran suit donc l'écriture : même fil, même session, même
 * éditeur déplacé, rien n'est recréé ni renvoyé.
 *
 * Deux signaux du `WebSocketPool`, deux gestes :
 * - `agent_canvas_admission_started` — Hector PREND le document du fil : le
 *   dock publie la scène du canvas pour ce fil, avant que le canvas ne se
 *   déploie. Un chargement DOCX froid marqué `requiresVisibleSlot` rejoint
 *   immédiatement l'assistant : SuperDoc doit mesurer le slot final, pas le
 *   parking du dock. Les admissions ordinaires ne déplacent pas l'écran ici.
 * - `agent_canvas_write_started` — Hector ÉCRIT : le dock bascule vers
 *   `/assistant?c=<fil>`. Le pool en garantit la transition réelle et
 *   l'idempotence par requête ou par job.
 *
 * ## La bascule reste forcée, la transition devient douce
 *
 * La première version naviguait DANS le signal : l'écran sautait d'un coup,
 * sans un mot, et l'avocat ne comprenait pas ce qui venait de se passer. On ne
 * lui demande toujours pas son avis — Hector écrit, l'écran suit — mais on lui
 * dit d'abord ce qui arrive. L'écriture POSE donc une annonce dans le dock, et
 * la bascule part au bout de `DELAI_DANNONCE_AVANT_LA_BASCULE_MS`.
 *
 * Le délai vit ici, et ici seulement : c'est le hook qui arme la minuterie, qui
 * l'annule et qui la rejoue. L'aide #1587 (`ouvrirConversationDansLAssistant`)
 * reste strictement synchrone — relève du registre, fermeture du dock,
 * navigation dans le même geste. Un `await` glissé DANS cette aide produirait
 * un commit où deux chats coexistent ; l'attente est donc AVANT elle, pas
 * dedans.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { publierLaScene } from '@/features/redaction/state/canvasStage';
import { useAssistantDockStore } from '@/stores/assistantDockStore';
import { useWebSocketPool } from '../providers/WebSocketPoolProvider';
import {
  annoncerLaBasculeDuDock,
  oublierLAnnonceDeBasculeDuDock,
} from '../state/basculeAnnoncee';
import {
  preserverBrouillonComposeurRelais,
  type BrouillonComposeur,
} from '../state/brouillonComposeurRelais';
import { preserverLeRetourDepuisLaBascule } from '../state/retourDepuisLaBascule';
import {
  doitBasculerVersLAssistant,
  doitPublierLaSceneDuDock,
  lireLaRouteDeLOnglet,
  type RouteDeLOnglet,
} from '../utils/doitBasculerVersLAssistant';
import { useOuvrirConversationDansLAssistant } from './useOuvrirConversationDansLAssistant';

/**
 * Temps laissé à l'avocat pour lire « Hector rédige… » avant que l'écran ne
 * change (#1588).
 *
 * Assez long pour que l'annonce se lise et que le fondu se voie, assez court
 * pour ne pas donner l'impression d'une application qui rame : l'avocat a
 * demandé un acte, il veut le voir s'écrire. Le bouton « Ouvrir maintenant » de
 * l'annonce coupe cette attente pour qui n'en veut pas.
 *
 * Un seul endroit à changer — le hook arme la minuterie, le bandeau accorde son
 * fondu sur la même valeur, les stories l'attendent.
 */
export const DELAI_DANNONCE_AVANT_LA_BASCULE_MS = 1_200;

/** Ce que le dock affiche au moment où il rend. */
export interface EtatDuDockPourLaBascule {
  /** Fil du dock ; `null` tant que le premier envoi n'a pas été confirmé. */
  readonly conversationId: string | null;
  /** Connexion du dock ; `null` tant qu'aucune session n'est ouverte. */
  readonly sessionKey: string | null;
  /** Dossier choisi dans le composeur du dock ; publié avec la scène. */
  readonly caseId: string | null;
  /** Lecture impérative du composeur juste avant son démontage. */
  readonly lireBrouillonComposeur: () => BrouillonComposeur;
}

/** L'annonce à peindre dans le dock pendant que le délai s'écoule. */
export interface AnnonceDeBascule {
  /** Fil ÉCRIT vers lequel l'écran s'apprête à partir. */
  readonly conversationId: string;
  /** Coupe l'attente et bascule tout de suite. */
  readonly ouvrirMaintenant: () => void;
}

/** L'annonce posée, telle que la minuterie la relit. */
interface AnnonceEnAttente {
  readonly conversationId: string;
  /** Route de l'onglet au moment du signal : le repère de retour et le contrôle d'abandon en dépendent. */
  readonly routeDOrigine: RouteDeLOnglet | null;
}

function decrireLaRoute(route: RouteDeLOnglet | null): string | null {
  return route === null ? null : `${route.pathname}${route.search}`;
}

/**
 * Abonne le dock aux signaux de prise et d'écriture du pool : publie la scène du
 * canvas quand `doitPublierLaSceneDuDock` le décide, et annonce puis bascule
 * vers `/assistant?c=<fil écrit>` quand `doitBasculerVersLAssistant` le décide.
 *
 * Rend l'annonce en cours, ou `null` quand aucune bascule n'est en attente.
 *
 * À appeler depuis `DossierCaseDiscoveryShell`, et depuis lui seul : un second
 * abonné naviguerait deux fois pour la même écriture.
 */
export function useBasculeDuDockVersLAssistant({
  conversationId,
  sessionKey,
  caseId,
  lireBrouillonComposeur,
}: EtatDuDockPourLaBascule): AnnonceDeBascule | null {
  const pool = useWebSocketPool();
  const ouvrirDansLAssistant = useOuvrirConversationDansLAssistant();

  // Les abonnements ne se refont pas à chaque rendu : un signal tombe d'un
  // événement réseau et doit trouver le fil, la connexion et le dossier
  // COURANTS. Recopiés en layout effect, donc déjà à jour au commit, avant
  // qu'une trame traitée après ce commit ne les lise. La route, elle, n'est PAS
  // recopiée : elle est lue au signal, dans le navigateur (`lireLaRouteDeLOnglet`).
  const courant = useRef({
    conversationId, sessionKey, caseId, ouvrirDansLAssistant, lireBrouillonComposeur,
  });
  useLayoutEffect(() => {
    courant.current = {
      conversationId, sessionKey, caseId, ouvrirDansLAssistant, lireBrouillonComposeur,
    };
  }, [caseId, conversationId, lireBrouillonComposeur, ouvrirDansLAssistant, sessionKey]);

  // Une bascule par montage : ouverture, action puis génération d'un même tour
  // portent chacune une clé neuve, que le Set du pool laisse passer.
  const basculeFaiteRef = useRef(false);

  // L'annonce vit deux fois : dans un ref, que la minuterie relit hors rendu et
  // qui fait foi pour décider ; dans un state, que le dock peint. Le ref est
  // aussi le jeton d'unicité — vidé avant d'ouvrir, il interdit qu'un clic
  // « Ouvrir maintenant » et l'échéance du délai naviguent tous les deux.
  const annonceRef = useRef<AnnonceEnAttente | null>(null);
  const [annonce, setAnnonce] = useState<AnnonceEnAttente | null>(null);
  const minuterieRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const retirerLAnnonce = useCallback((): AnnonceEnAttente | null => {
    if (minuterieRef.current !== null) {
      clearTimeout(minuterieRef.current);
      minuterieRef.current = null;
    }
    const enAttente = annonceRef.current;
    annonceRef.current = null;
    setAnnonce(null);
    // Le registre lu par le pool suit exactement le ref : il n'y a pas d'autre
    // porte de sortie de l'annonce que celle-ci, départ comme abandon.
    if (enAttente !== null) oublierLAnnonceDeBasculeDuDock(enAttente.conversationId);
    return enAttente;
  }, []);

  const relayerLeDockVersLAssistant = useCallback((
    conversationIdDuMontage: string,
    routeDOrigine: RouteDeLOnglet | null,
  ) => {
    const dock = courant.current;
    // Cette copie précède la fermeture synchrone : texte et fichiers vivent
    // dans le composant que l'aide va justement démonter. Lue à l'échéance, et
    // non au signal : l'avocat a pu continuer d'écrire pendant l'annonce.
    preserverBrouillonComposeurRelais(conversationIdDuMontage, dock.lireBrouillonComposeur());
    // D'où l'on vient, pour que l'assistant offre le chemin du retour.
    preserverLeRetourDepuisLaBascule(conversationIdDuMontage, decrireLaRoute(routeDOrigine));
    dock.ouvrirDansLAssistant(conversationIdDuMontage);
  }, []);

  const acheverLaBascule = useCallback(() => {
    const enAttente = retirerLAnnonce();
    if (enAttente === null) return;

    // Trois raisons d'abandonner, toutes relues MAINTENANT et pas au signal :
    // l'avocat a eu le temps d'agir pendant l'attente, et son geste prime.
    // 1. Il a fermé le dock — l'y emmener malgré tout contredirait ce geste.
    if (!useAssistantDockStore.getState().isOpen) return;
    // 2. Il a navigué de lui-même : le déplacer alors serait lui reprendre la
    //    main qu'il vient de prendre. `basculeFaiteRef` reste posée — on ne le
    //    poursuit pas non plus à l'écriture suivante du même tour.
    const routeActuelle = lireLaRouteDeLOnglet();
    if (decrireLaRoute(routeActuelle) !== decrireLaRoute(enAttente.routeDOrigine)) return;

    // Le fil ÉCRIT, et non celui du dock : avant confirmation, le dock n'a
    // encore aucun identifiant et c'est la connexion qui a désigné ce fil.
    relayerLeDockVersLAssistant(enAttente.conversationId, enAttente.routeDOrigine);
  }, [relayerLeDockVersLAssistant, retirerLAnnonce]);

  /**
   * Bascule sans annonce pour le seul montage qui ne peut pas commencer hors
   * écran. Le chemin conserve les mêmes relais de composeur et de retour que
   * l'échéance normale ; seule l'attente de 1,2 s est interdite ici.
   */
  const basculerAvantLeMontage = useCallback((
    conversationIdDuMontage: string,
    routeDOrigine: RouteDeLOnglet | null,
  ) => {
    relayerLeDockVersLAssistant(conversationIdDuMontage, routeDOrigine);
  }, [relayerLeDockVersLAssistant]);

  // L'annonce nomme un fil : si le dock change de conversation pendant
  // l'attente, l'emmener vers le fil annoncé le sortirait de celle qu'il vient
  // d'ouvrir. Layout effect, comme la recopie des valeurs courantes : la
  // décision doit être prise au commit, avant qu'une trame ne la lise.
  useLayoutEffect(() => {
    const enAttente = annonceRef.current;
    if (enAttente === null || conversationId === null) return;
    if (conversationId !== enAttente.conversationId) retirerLAnnonce();
  }, [conversationId, retirerLAnnonce]);

  // Un dock démonté ne parle plus pour l'écran : la minuterie part avec lui.
  // Le nettoyage ignore la valeur rendue par `retirerLAnnonce` — React exige un
  // `Destructor`, et l'annonce retirée n'intéresse personne à ce stade.
  useEffect(() => () => { retirerLAnnonce(); }, [retirerLAnnonce]);

  useEffect(() => pool.events.on('agent_canvas_admission_started', (signal) => {
    const dock = courant.current;
    const route = lireLaRouteDeLOnglet();
    const doitRejoindreLeSlotAvantMontage = signal.requiresVisibleSlot === true
      && doitBasculerVersLAssistant({
        signal,
        conversationIdDuDock: dock.conversationId,
        sessionKeyDuDock: dock.sessionKey,
        route,
        dejaBascule: basculeFaiteRef.current,
      });
    const doitPublier = doitPublierLaSceneDuDock({
      signal,
      conversationIdDuDock: dock.conversationId,
      sessionKeyDuDock: dock.sessionKey,
      route,
    });
    if (!doitPublier && !doitRejoindreLeSlotAvantMontage) return;
    // Même filet que la bascule : un dock fermé ne parle plus pour l'écran.
    if (!useAssistantDockStore.getState().isOpen) return;
    // Publiée dans l'émission même : le pool déploie le canvas juste après, dans
    // la même tâche, et la résidence doit y lire ce fil — pas celui qu'un écran
    // précédent a laissé sur la scène.
    publierLaScene({ conversationId: signal.conversationId, caseId: dock.caseId });
    if (!doitRejoindreLeSlotAvantMontage) return;
    // La marque est posée avant `openCanvas`, par le même appel synchrone du
    // pool. La route peut donc committer son slot pendant que le handler attend
    // explicitement ce slot, au lieu de créer SuperDoc dans le parking.
    basculeFaiteRef.current = true;
    basculerAvantLeMontage(signal.conversationId, route);
  }), [basculerAvantLeMontage, pool]);

  useEffect(() => pool.events.on('agent_canvas_write_started', (signal) => {
    const dock = courant.current;
    if (!doitBasculerVersLAssistant({
      signal,
      conversationIdDuDock: dock.conversationId,
      sessionKeyDuDock: dock.sessionKey,
      route: lireLaRouteDeLOnglet(),
      dejaBascule: basculeFaiteRef.current,
    })) return;
    // Filet : un dock que l'avocat vient de fermer n'est pas encore démonté, et
    // l'y emmener malgré tout contredirait son geste.
    if (!useAssistantDockStore.getState().isOpen) return;
    // Posée AVANT l'annonce : une seconde écriture traitée pendant le délai ne
    // doit ni réarmer la minuterie ni naviguer une seconde fois — pas plus
    // qu'un abandon ne doit se rejouer à chaque écriture suivante.
    basculeFaiteRef.current = true;
    const enAttente: AnnonceEnAttente = {
      conversationId: signal.conversationId,
      routeDOrigine: lireLaRouteDeLOnglet(),
    };
    annonceRef.current = enAttente;
    setAnnonce(enAttente);
    // Déclaré ICI, dans l'émission même : l'appelant du pool enchaîne, dans le
    // MÊME bloc synchrone, sur `laisserLEcranRejoindreLeFil`. Celui-ci doit y
    // lire que l'écran rejoint ce fil, sans quoi le premier `write_markdown`
    // en `replace` lancé depuis le dock se ferait refuser sur un document
    // encore parqué — la route, elle, ne changera que dans 1,2 s (#1586).
    annoncerLaBasculeDuDock(signal.conversationId);
    minuterieRef.current = setTimeout(acheverLaBascule, DELAI_DANNONCE_AVANT_LA_BASCULE_MS);
  }), [acheverLaBascule, pool]);

  return useMemo(
    () => (annonce === null
      ? null
      : { conversationId: annonce.conversationId, ouvrirMaintenant: acheverLaBascule }),
    [acheverLaBascule, annonce],
  );
}
